import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogIn, LogOut, Package, MessageSquare, Star, BarChart3,
  Plus, Pencil, Trash2, X, Save, Eye, EyeOff, Cake, Cookie,
  CakeSlice, Croissant, ChevronLeft, Mail, Phone, Calendar,
  Settings, MapPin, Instagram, MessageCircle
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("adminToken", res.data.token);
      localStorage.setItem("adminUser", res.data.username);
      onLogin(res.data.token);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5F8] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl text-[#4A3540] font-semibold">rcakecottage</h1>
          <p className="text-[#4A3540]/60 mt-2">Admin Dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-[#FFCCE0] shadow-lg" data-testid="admin-login-form">
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#4A3540] mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                className="w-full border-[#FFCCE0] rounded-xl"
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4A3540] mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="w-full border-[#FFCCE0] rounded-xl pr-10"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A3540]/50"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E91E8C] text-white py-6 rounded-full font-medium hover:bg-[#D11A7D] transition-all"
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : <><LogIn size={18} className="mr-2 inline" /> Sign In</>}
            </Button>
          </div>
        </form>
        
        <p className="text-center text-[#4A3540]/50 text-sm mt-6">
          Default: admin / rcake2024
        </p>
      </motion.div>
    </div>
  );
};

// Dashboard Stats Card
const StatsCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white p-6 rounded-2xl border border-[#FFCCE0]"
  >
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <div>
        <p className="text-[#4A3540]/60 text-sm">{label}</p>
        <p className="font-serif text-2xl text-[#4A3540] font-semibold">{value}</p>
      </div>
    </div>
  </motion.div>
);

// Product Form Modal
const ProductModal = ({ product, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    product || {
      name: "",
      description: "",
      price: "",
      category: "cakes",
      image_url: "",
      is_featured: false,
    }
  );

  const categories = [
    { id: "cakes", name: "Cakes", icon: Cake },
    { id: "cupcakes", name: "Cupcakes", icon: CakeSlice },
    { id: "cookies", name: "Cookies", icon: Cookie },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl text-[#4A3540] font-semibold">
            {product ? "Edit Product" : "Add New Product"}
          </h3>
          <button onClick={onClose} className="text-[#4A3540]/50 hover:text-[#4A3540]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Product Name</label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Chocolate Truffle Cake"
              required
              className="w-full border-[#FFCCE0] rounded-xl"
              data-testid="product-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your delicious creation..."
              required
              rows={3}
              className="w-full border-[#FFCCE0] rounded-xl resize-none"
              data-testid="product-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#4A3540] mb-2">Price (€)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="0.00"
                required
                className="w-full border-[#FFCCE0] rounded-xl"
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4A3540] mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 border border-[#FFCCE0] rounded-xl bg-white text-[#4A3540]"
                data-testid="product-category-select"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Image URL</label>
            <Input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
              required
              className="w-full border-[#FFCCE0] rounded-xl"
              data-testid="product-image-input"
            />
            {formData.image_url && (
              <div className="mt-2 aspect-video rounded-xl overflow-hidden bg-[#FFF0F5]">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-5 h-5 rounded border-[#FFCCE0] text-[#E91E8C]"
              data-testid="product-featured-checkbox"
            />
            <label htmlFor="featured" className="text-sm text-[#4A3540]">Mark as Featured</label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#FFE4EF] text-[#4A3540] py-5 rounded-full font-medium hover:bg-[#FFCCE0]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#E91E8C] text-white py-5 rounded-full font-medium hover:bg-[#D11A7D]"
              data-testid="product-save-btn"
            >
              <Save size={18} className="mr-2 inline" />
              {product ? "Update" : "Add Product"}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Testimonial Form Modal
const TestimonialModal = ({ testimonial, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    testimonial || {
      customer_name: "",
      content: "",
      rating: 5,
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-6 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="testimonial-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl text-[#4A3540] font-semibold">
            {testimonial ? "Edit Testimonial" : "Add Testimonial"}
          </h3>
          <button onClick={onClose} className="text-[#4A3540]/50 hover:text-[#4A3540]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Customer Name</label>
            <Input
              type="text"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="e.g., Sarah M."
              required
              className="w-full border-[#FFCCE0] rounded-xl"
              data-testid="testimonial-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Review</label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="What did they say about your treats?"
              required
              rows={4}
              className="w-full border-[#FFCCE0] rounded-xl resize-none"
              data-testid="testimonial-content-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4A3540] mb-2">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="p-1"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    size={28}
                    className={star <= formData.rating ? "fill-[#E91E8C] text-[#E91E8C]" : "text-[#FFCCE0]"}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#FFE4EF] text-[#4A3540] py-5 rounded-full font-medium hover:bg-[#FFCCE0]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#E91E8C] text-white py-5 rounded-full font-medium hover:bg-[#D11A7D]"
              data-testid="testimonial-save-btn"
            >
              <Save size={18} className="mr-2 inline" />
              {testimonial ? "Update" : "Add Testimonial"}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Main Admin Dashboard
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, testimonialsRes, messagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/products`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/settings`),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setTestimonials(testimonialsRes.data);
      setMessages(messagesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout();
      }
      console.error("Error fetching data:", error);
    }
  };

  // Settings update
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, authHeaders);
      toast.success("Settings updated!");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Product CRUD
  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, authHeaders);
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success("Product added!");
      }
      fetchData();
      setShowProductModal(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, authHeaders);
      toast.success("Product deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  // Testimonial CRUD
  const handleSaveTestimonial = async (testimonialData) => {
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonial.id}`, testimonialData, authHeaders);
        toast.success("Testimonial updated!");
      } else {
        await axios.post(`${API}/testimonials`, testimonialData);
        toast.success("Testimonial added!");
      }
      fetchData();
      setShowTestimonialModal(false);
      setEditingTestimonial(null);
    } catch (error) {
      toast.error("Failed to save testimonial");
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, authHeaders);
      toast.success("Testimonial deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/contact/${id}`, authHeaders);
      toast.success("Message deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const tabs = [
    { id: "overview", name: "Overview", icon: BarChart3 },
    { id: "products", name: "Products", icon: Package },
    { id: "testimonials", name: "Testimonials", icon: Star },
    { id: "messages", name: "Messages", icon: MessageSquare },
    { id: "settings", name: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#FFF5F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#FFCCE0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-[#4A3540]/60 hover:text-[#E91E8C] transition-colors">
              <ChevronLeft size={20} />
              <span className="text-sm">Back to Site</span>
            </a>
            <div className="w-px h-6 bg-[#FFCCE0]" />
            <h1 className="font-serif text-xl text-[#4A3540] font-semibold">Admin Dashboard</h1>
          </div>
          <Button
            onClick={onLogout}
            className="bg-[#FFE4EF] text-[#4A3540] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#FFCCE0]"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2 inline" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-[#E91E8C] text-white"
                    : "bg-white text-[#4A3540] border border-[#FFCCE0] hover:bg-[#FFE4EF]"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatsCard icon={Package} label="Total Products" value={stats.total_products} color="bg-[#E91E8C]" />
              <StatsCard icon={Star} label="Testimonials" value={stats.total_testimonials} color="bg-[#5BC0DE]" />
              <StatsCard icon={MessageSquare} label="Messages" value={stats.total_messages} color="bg-[#4A3540]" />
              <StatsCard icon={Cake} label="Cakes" value={stats.products_by_category?.cakes || 0} color="bg-[#E91E8C]/80" />
            </div>

            <div className="bg-white rounded-2xl border border-[#FFCCE0] p-6">
              <h3 className="font-serif text-lg text-[#4A3540] font-semibold mb-4">Products by Category</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats.products_by_category || {}).map(([category, count]) => (
                  <div key={category} className="bg-[#FFF0F5] rounded-xl p-4 text-center">
                    <p className="text-2xl font-serif text-[#E91E8C] font-semibold">{count}</p>
                    <p className="text-sm text-[#4A3540]/60 capitalize">{category}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Products Tab */}
        {activeTab === "products" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#4A3540] font-semibold">Products</h2>
              <Button
                onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                className="bg-[#E91E8C] text-white px-5 py-2 rounded-full font-medium hover:bg-[#D11A7D]"
                data-testid="add-product-btn"
              >
                <Plus size={18} className="mr-2 inline" />
                Add Product
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl border border-[#FFCCE0] overflow-hidden"
                  data-testid={`admin-product-${product.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#FFF0F5]">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#E91E8C] uppercase">{product.category}</span>
                      {product.is_featured && (
                        <span className="text-xs bg-[#FFE4EF] text-[#5BC0DE] px-2 py-1 rounded-full">Featured</span>
                      )}
                    </div>
                    <h3 className="font-serif text-lg text-[#4A3540] font-semibold">{product.name}</h3>
                    <p className="text-[#4A3540]/60 text-sm mt-1 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="font-serif text-xl text-[#E91E8C] font-semibold">€{product.price.toFixed(2)}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                          className="p-2 bg-[#FFE4EF] rounded-full hover:bg-[#FFCCE0] transition-colors"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil size={16} className="text-[#4A3540]" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="p-2 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Testimonials Tab */}
        {activeTab === "testimonials" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#4A3540] font-semibold">Testimonials</h2>
              <Button
                onClick={() => { setEditingTestimonial(null); setShowTestimonialModal(true); }}
                className="bg-[#E91E8C] text-white px-5 py-2 rounded-full font-medium hover:bg-[#D11A7D]"
                data-testid="add-testimonial-btn"
              >
                <Plus size={18} className="mr-2 inline" />
                Add Testimonial
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-2xl border border-[#FFCCE0] p-6"
                  data-testid={`admin-testimonial-${testimonial.id}`}
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-[#E91E8C] text-[#E91E8C]" />
                    ))}
                  </div>
                  <p className="text-[#4A3540]/70 text-sm italic mb-4">"{testimonial.content}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#FFE4EF] rounded-full flex items-center justify-center">
                        <span className="font-serif text-[#E91E8C] text-sm font-semibold">
                          {testimonial.customer_name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#4A3540]">{testimonial.customer_name}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingTestimonial(testimonial); setShowTestimonialModal(true); }}
                        className="p-2 bg-[#FFE4EF] rounded-full hover:bg-[#FFCCE0] transition-colors"
                        data-testid={`edit-testimonial-${testimonial.id}`}
                      >
                        <Pencil size={14} className="text-[#4A3540]" />
                      </button>
                      <button
                        onClick={() => handleDeleteTestimonial(testimonial.id)}
                        className="p-2 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                        data-testid={`delete-testimonial-${testimonial.id}`}
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-serif text-2xl text-[#4A3540] font-semibold mb-6">Contact Messages</h2>

            {messages.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#FFCCE0] p-12 text-center">
                <MessageSquare size={48} className="text-[#FFCCE0] mx-auto mb-4" />
                <p className="text-[#4A3540]/60">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className="bg-white rounded-2xl border border-[#FFCCE0] p-6"
                    data-testid={`admin-message-${message.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <h3 className="font-semibold text-[#4A3540]">{message.name}</h3>
                          <span className="text-xs text-[#4A3540]/50 flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(message.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#4A3540]/70 mb-3">
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {message.email}
                          </span>
                          {message.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {message.phone}
                            </span>
                          )}
                        </div>
                        <p className="text-[#4A3540]/80">{message.message}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="p-2 bg-red-100 rounded-full hover:bg-red-200 transition-colors flex-shrink-0"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && settings && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="font-serif text-2xl text-[#4A3540] font-semibold mb-6">Site Settings</h2>

            <div className="bg-white rounded-2xl border border-[#FFCCE0] p-6 max-w-2xl">
              <div className="space-y-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#4A3540] mb-2">
                    <MessageCircle size={16} className="text-[#25D366]" />
                    WhatsApp Number
                  </label>
                  <Input
                    type="text"
                    value={settings.whatsapp}
                    onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                    placeholder="+31617354769"
                    className="w-full border-[#FFCCE0] rounded-xl"
                    data-testid="settings-whatsapp"
                  />
                  <p className="text-xs text-[#4A3540]/50 mt-1">Include country code (e.g., +31 for Netherlands)</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#4A3540] mb-2">
                    <Phone size={16} className="text-[#E91E8C]" />
                    Phone Number (Display)
                  </label>
                  <Input
                    type="text"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    placeholder="+31617354769"
                    className="w-full border-[#FFCCE0] rounded-xl"
                    data-testid="settings-phone"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#4A3540] mb-2">
                    <Mail size={16} className="text-[#E91E8C]" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    placeholder="rcakecottage23@gmail.com"
                    className="w-full border-[#FFCCE0] rounded-xl"
                    data-testid="settings-email"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#4A3540] mb-2">
                    <MapPin size={16} className="text-[#E91E8C]" />
                    Address / Location
                  </label>
                  <Textarea
                    value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                    placeholder="Dr G Knuttelpark, 77, 2552LK, Den Haag"
                    rows={2}
                    className="w-full border-[#FFCCE0] rounded-xl resize-none"
                    data-testid="settings-address"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-[#4A3540] mb-2">
                    <Instagram size={16} className="text-[#E4405F]" />
                    Instagram Username
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-[#4A3540]/50">@</span>
                    <Input
                      type="text"
                      value={settings.instagram}
                      onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                      placeholder="rcake_cottage"
                      className="flex-1 border-[#FFCCE0] rounded-xl"
                      data-testid="settings-instagram"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full bg-[#E91E8C] text-white py-5 rounded-full font-medium hover:bg-[#D11A7D] transition-all"
                  data-testid="settings-save-btn"
                >
                  {savingSettings ? "Saving..." : <><Save size={18} className="mr-2 inline" /> Save Settings</>}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showProductModal && (
          <ProductModal
            product={editingProduct}
            onSave={handleSaveProduct}
            onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
          />
        )}
        {showTestimonialModal && (
          <TestimonialModal
            testimonial={editingTestimonial}
            onSave={handleSaveTestimonial}
            onClose={() => { setShowTestimonialModal(false); setEditingTestimonial(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Admin App
function AdminApp() {
  const [token, setToken] = useState(localStorage.getItem("adminToken"));

  useEffect(() => {
    // Verify token on mount
    if (token) {
      axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {
        handleLogout();
      });
    }
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setToken(null);
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      {token ? (
        <AdminDashboard token={token} onLogout={handleLogout} />
      ) : (
        <LoginForm onLogin={setToken} />
      )}
    </>
  );
}

export default AdminApp;
